import { useCallback, useMemo } from "react";
import { useQueries, useQueryClient } from "@tanstack/react-query";
import { userPreferencesService } from "../services/workflows.service";
import { useAuthStore } from "../store/auth.store";
import {
  getPathologistQueueDefaultPreferenceKey,
  type PathologistQueueDefault,
} from "../pages/workflows/pathologistQueueDefaultPreference";
import {
  PATHOLOGIST_QUEUE_VIEW_TABS,
  type PathologistQueueViewTab,
} from "../pages/workflows/pathologistQueueViewPreference";

const saveChains = new Map<string, Promise<void>>();

export function usePathologistQueueDefaultPreference() {
  const userId = useAuthStore((state) => state.profile?.id);
  const queryClient = useQueryClient();
  const queries = useQueries({
    queries: PATHOLOGIST_QUEUE_VIEW_TABS.map((tab) => ({
      queryKey: [
        "user-preference",
        userId,
        getPathologistQueueDefaultPreferenceKey(tab),
      ],
      queryFn: () =>
        userPreferencesService.get(
          getPathologistQueueDefaultPreferenceKey(tab)
        ),
      enabled: Boolean(userId),
      staleTime: Infinity,
    })),
  });
  const rawDefaults = useMemo(
    () =>
      Object.fromEntries(
        PATHOLOGIST_QUEUE_VIEW_TABS.map((tab, index) => [
          tab,
          queries[index].data,
        ])
      ) as Record<PathologistQueueViewTab, unknown>,
    [queries]
  );
  const hydratedTabs = useMemo(
    () =>
      Object.fromEntries(
        PATHOLOGIST_QUEUE_VIEW_TABS.map((tab, index) => [
          tab,
          Boolean(userId) && queries[index].isFetched,
        ])
      ) as Record<PathologistQueueViewTab, boolean>,
    [queries, userId]
  );
  const errorTabs = useMemo(
    () =>
      Object.fromEntries(
        PATHOLOGIST_QUEUE_VIEW_TABS.map((tab, index) => [
          tab,
          queries[index].isError,
        ])
      ) as Record<PathologistQueueViewTab, boolean>,
    [queries]
  );
  const getRawDefault = useCallback(
    (tab: PathologistQueueViewTab): unknown => rawDefaults[tab],
    [rawDefaults]
  );

  const saveDefault = useCallback(
    async (
      tab: PathologistQueueViewTab,
      value: PathologistQueueDefault
    ): Promise<void> => {
      const query = queries[PATHOLOGIST_QUEUE_VIEW_TABS.indexOf(tab)];
      if (!userId || !query?.isFetched || query.isError) {
        throw new Error("Default preference is unavailable");
      }
      const preferenceKey = getPathologistQueueDefaultPreferenceKey(tab);
      const chainKey = `${userId}:${tab}`;
      const queryKey = ["user-preference", userId, preferenceKey];
      const prior = saveChains.get(chainKey) ?? Promise.resolve();
      const saving = prior
        .catch(() => undefined)
        .then(async () => {
          if (useAuthStore.getState().profile?.id !== userId) {
            throw new Error("Authenticated user changed before saving default");
          }
          await userPreferencesService.save(preferenceKey, value);
          queryClient.setQueryData(queryKey, value);
        });
      saveChains.set(chainKey, saving);
      try {
        await saving;
      } finally {
        if (saveChains.get(chainKey) === saving) saveChains.delete(chainKey);
      }
    },
    [queries, queryClient, userId]
  );

  return {
    rawDefault: rawDefaults.review,
    rawDefaults,
    getRawDefault,
    hydratedTabs,
    errorTabs,
    isHydrated: hydratedTabs.review,
    isError: errorTabs.review,
    saveDefault,
  };
}
